import { useEffect, useState } from 'react';
import { Dimensions, findNodeHandle, Keyboard, Platform, TextInput, UIManager } from 'react-native';

const SAFE_MARGIN = 16;

/**
 * Returns the dp the layout needs to shift up so the focused TextInput stays
 * visible above the keyboard. Returns 0 when the input already sits above the
 * keyboard's top edge.
 *
 * Used in place of `KeyboardAvoidingView` because the latter is unreliable
 * in Android 15 edge-to-edge mode.
 *
 * @param {boolean} enabled When false, returns 0 and skips all listeners.
 * @returns {number} Required upward offset in dp; 0 when no push is needed.
 */
const useKeyboardOverlap = (enabled) => {
  const [overlap, setOverlap] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const setIfChanged = (next) => setOverlap((prev) => (prev === next ? prev : next));

    const handleShow = (event) => {
      const kbHeight = event?.endCoordinates?.height ?? 0;
      if (!kbHeight) {
        setIfChanged(0);
        return;
      }

      const kbTop = Dimensions.get('window').height - kbHeight;
      const focused = TextInput.State.currentlyFocusedInput?.();
      const handle = focused && findNodeHandle(focused);
      if (!handle) {
        setIfChanged(0);
        return;
      }

      UIManager.measureInWindow(handle, (x, y, w, h) => {
        setIfChanged(Math.max(0, y + h + SAFE_MARGIN - kbTop));
      });
    };

    const showSub = Keyboard.addListener(showEvent, handleShow);
    // `keyboardDidChangeFrame` covers IME changes mid-session (text↔numeric
    // layouts, autocomplete bar, focus moving to a different input on Android)
    // so the overlap stays accurate without a full hide/show cycle.
    const changeSub = Keyboard.addListener('keyboardDidChangeFrame', handleShow);
    const hideSub = Keyboard.addListener(hideEvent, () => setIfChanged(0));

    return () => {
      showSub.remove();
      changeSub.remove();
      hideSub.remove();
    };
  }, [enabled]);

  return overlap;
};

export default useKeyboardOverlap;
